import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { IPersonaProps, ITag } from "office-ui-fabric-react";
import { FileTransferApiService } from "../../services/NewformaApi/FileTransferApiService";
import { OutlookApiService } from "../../services/OutlookApiService";
import { IProjectData } from "@src/models/shared/IProjectData";
import { LogFileTransferParams } from "../../models/workflow/fileTransfer/LogFileTransferParams";
import { Log, Logger } from "../../services/Logger";
import { DateHelper } from "../../helpers/DateHelpers";
import { makeStyles, tokens } from "@fluentui/react-components";
import { ProjectPickerFluent9 } from "../shared/projectPicker/ProjectPickerFluent9";
import { useProjects } from "../shared/projectPicker/ProjectsContext";
import { SmartFilingManagerFluent9 } from "@src/services/SmartFiling/SmartFilingManagerFluent9";
import { InvalidateCacheService } from "@src/services/NewformaApi/InvalidateCacheService";
import { KeywordListType, DetailedKeyword } from "../../models/ProjectKeywordsResponse";
import KeywordsFluent9 from "../shared/keywordsDropdown/KeywordsFluent9";
import { IProjectsService } from "@src/services/NewformaApi/IProjectsService";
import { Keyword } from "../../models/Keyword";
import PurposeDropdownFluent9 from "../shared/purposeDropdown/PurposeDropdownFluent9";
import AttachmentsFluent9 from "../shared/attachments/AttachmentsFluent9";
import { AttachmentItem } from "../../models/shared/AttachmentList";
import { MailboxItem, OfficeWrapper } from "../../services/OfficeWrapper";
import { FormValidationHelpers } from "../../helpers/FormValidationHelpers";
import { AttachmentDataHelpers } from "../../helpers/AttachmentDataHelpers";
import { NewformaPanel } from "../_base/Panel";
import { NewformaButton } from "../_base/Button";
import { NewformaInput } from "../_base/NewformaInput";
import { NewformaToastIntent } from "../_base/Toast";
import FromAndToFluent9 from "../shared/fromAndToComponent/FromAndToFluent9";
import RemarksFluent9 from "../shared/remarks/RemarksFluent9";
import { ConfigurationService } from "../../services/ConfigurationService";
import { MsGraphApiService } from "../../services/MsGraphApiService";
import { StorageWrapper } from "../../services/StorageWrapper";
import { FileTransferProgressPage } from "./FileTransferProgressPage";
import {
    MoreOptionsProjectConfig,
    MoreOptionsSettings,
    MoreOptionsFluent9Component,
} from "../shared/moreOption/MoreOptionsFluent9Component";
import CancelFileTransferDialog from "../dialogs/fileTransfer/CancelFileTransferDialog";

const useStyles = makeStyles({
    formFields: {
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalL, // 16px
    },
});

interface FileTransferFluent9ComponentProps extends LocalizeContextProps {
    projectsService: IProjectsService;
    outlookApiService: OutlookApiService;
    fileTransferApiService: FileTransferApiService;
    smartFilingManager: SmartFilingManagerFluent9;
    invalidateCacheService: InvalidateCacheService;
    officeWrapper: OfficeWrapper;
    mailboxItem: MailboxItem | null;
    isFilePathInAttachmentsSupported: boolean;
    isNL1221Supported: boolean;
    logger: Logger;
    formValidationHelpers: FormValidationHelpers;
    configService?: ConfigurationService;
    msGraphApiService: MsGraphApiService;
    isOpen: boolean;
    isSendFilesFromProjectSupported: boolean;
    isGlobalProjectSearchSupported: boolean;
    storageWrapper: StorageWrapper;
    onClose: () => void;
    onShowToast: (message: string | null, type: NewformaToastIntent) => void;
}

const FileTransferFluent9ComponentHooks: React.FC<FileTransferFluent9ComponentProps> = (props) => {
    const styles = useStyles();
    const projectsContext = useProjects();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fileUploadCount, setFileUploadCount] = useState(0);
    const [fileUploadCompletedCount, setFileUploadCompletedCount] = useState(0);
    const [selectedProject, setSelectedProject] = useState<IProjectData | undefined>(undefined);
    const [selectedKeywords, setSelectedKeywords] = useState<DetailedKeyword[]>([]);
    const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
    const [selectedProjectAttachments, setSelectedProjectAttachments] = useState<AttachmentItem[]>([]);
    const [from, setFrom] = useState<IPersonaProps[]>([]);
    const [to, setTo] = useState<IPersonaProps[]>([]);
    const [cc, setCc] = useState<IPersonaProps[]>([]);
    const [showAttachmentError, setShowAttachmentError] = useState(false);
    const [showProjectError, setShowProjectError] = useState(false);
    const [subject, setSubject] = useState("");
    const [showSubjectError, setShowSubjectError] = useState(false);
    const [remarks, setRemarks] = useState("");
    const [selectedPurpose, setSelectedPurpose] = useState<Keyword | null>(null);
    const [showPurposeField, setShowPurposeField] = useState(false);
    const [showPurposeError, setShowPurposeError] = useState(false);
    const [isLoadingProjectSettings, setIsLoadingProjectSettings] = useState(false);
    const [shouldClearEmails, setShouldClearEmails] = useState(false);
    const [areToAndCCSame, setAreToAndCCSame] = useState(false);
    const [moreOptionsSettings, setMoreOptionsSettings] = useState<MoreOptionsSettings>({
        expireDateSelection: {
            Checked: false,
            SelectedOption: "",
            SelectedDate: undefined,
        },
        reminderDateSelection: {
            Checked: false,
            SelectedOption: "",
            SelectedDate: undefined,
        },
        allowPartialDownload: true,
        requireNixLoginForDownload: false,
    });
    const [moreOptionsProjectConfig, setMoreOptionsProjectConfig] = useState<MoreOptionsProjectConfig>({
        defaultExpireDays: 0,
        transferExpiresByDefault: false,
        allowPartialDownload: false,
        requireNixLoginForDownload: false,
    });
    const [projectChangeToken, setProjectChangeToken] = useState(0);
    const [attachmentsPopulateKey, setAttachmentsPopulateKey] = useState(0);
    const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
    const [isCancelInProgress, setIsCancelInProgress] = useState(false);
    // Project files are committed server-side in POST /transfer, so there is no upload to abort
    const [isProjectFilesOnlyTransfer, setIsProjectFilesOnlyTransfer] = useState(false);

    const previousProjectRef = useRef<IProjectData | undefined>(undefined);
    const prevIsOpenRef = useRef<boolean>(props.isOpen);
    const prevMailboxItemRef = useRef<MailboxItem | null>(props.mailboxItem);
    const isCancelRequestedRef = useRef(false);
    const isTransferFinishedRef = useRef(false);

    const populateAttachments = useCallback(() => {
        const { attachmentsNoCloud } = AttachmentDataHelpers.partitionAttachments(
            props.officeWrapper.getCurrentEmailAttachmentDetails()
        );
        setAttachmentsPopulateKey((k) => k + 1);
        setAttachments(attachmentsNoCloud);
    }, [props.officeWrapper]);

    const removeNewformaAutoFileEmail = useCallback((recipients?: Office.EmailAddressDetails[]): IPersonaProps[] => {
        return (recipients ?? [])
            .map((recipient) => recipient.emailAddress)
            .filter((email) => !email.toLowerCase().endsWith("newforma.email"))
            .map((email) => ({ text: email }));
    }, []);

    const populateFromAndTo = useCallback(() => {
        if (shouldClearEmails) {
            return;
        }

        const senderEmail = props.officeWrapper.getSenderEmailAddress();
        const ccEmails = props.officeWrapper.getCurrentEmailCcRecipients();

        const currentUserEmail = props.officeWrapper.userProfileEmailAddress;
        const nextFrom = currentUserEmail ? [{ text: currentUserEmail }] : [];
        const nextTo = senderEmail ? [{ text: senderEmail }] : [];
        const nextCc = removeNewformaAutoFileEmail(ccEmails);

        setFrom(nextFrom);
        setTo(nextTo);
        setCc(nextCc);
    }, [props.officeWrapper, removeNewformaAutoFileEmail, shouldClearEmails]);

    const populateRemarks = useCallback(async () => {
        try {
            const { inlineAttachments } = AttachmentDataHelpers.partitionAttachments(
                props.officeWrapper.getCurrentEmailAttachmentDetails()
            );
            const body = await AttachmentDataHelpers.getHtmlBodyWithInlineAttachments(
                props.msGraphApiService,
                props.outlookApiService,
                props.isNL1221Supported,
                inlineAttachments
            );
            setRemarks(body);
        } catch (error) {
            Log.error("Failed to populate remarks from email body", error);
        }
    }, [props.officeWrapper, props.msGraphApiService, props.outlookApiService, props.isNL1221Supported]);

    const loadProjectSettings = useCallback(
        async (projectNrn: string) => {
            setIsLoadingProjectSettings(true);
            try {
                const projectSettings = await props.projectsService.getProjectSettings(projectNrn);
                const shouldShowPurposeField = projectSettings?.transfer?.transferWithTransmittal ?? true;

                setShowPurposeField(shouldShowPurposeField);

                if (projectSettings?.transfer) {
                    const transfer = projectSettings.transfer;
                    setMoreOptionsProjectConfig({
                        defaultExpireDays: transfer.defaultExpireDays ?? 0,
                        transferExpiresByDefault: transfer.transferExpiresByDefault,
                        allowPartialDownload: transfer.allowPartialDownload,
                        requireNixLoginForDownload: transfer.requireNixLoginForDownload,
                    });
                }
            } catch (error) {
                Log.error("Failed to load project settings", error);
                setShowPurposeField(false);
            } finally {
                setIsLoadingProjectSettings(false);
            }
        },
        [props.projectsService]
    );

    useEffect(() => {
        previousProjectRef.current = selectedProject;
    }, []);

    // Load projects and populate attachments when panel opens
    useEffect(() => {
        const prevIsOpen = prevIsOpenRef.current;
        prevIsOpenRef.current = props.isOpen;

        if (prevIsOpen !== props.isOpen && props.isOpen) {
            projectsContext?.loadProjects(() => props.projectsService.getProjectsSupportingFileTransfer());
            populateAttachments();
            // Reopening during an in-progress transfer repopulates local files from the email; project files must reset too.
            setSelectedProjectAttachments([]);
            populateFromAndTo();
            setSubject(props.mailboxItem?.subject ?? "");
            populateRemarks();
        }
    }, [props.isOpen, props.projectsService, projectsContext, populateAttachments, populateFromAndTo, populateRemarks]);

    // Handle project selection changes
    useEffect(() => {
        if (previousProjectRef.current !== selectedProject) {
            previousProjectRef.current = selectedProject;

            if (selectedProject) {
                setShowProjectError(false);
                setProjectChangeToken((t) => t + 1);
                loadProjectSettings(selectedProject.nrn);
            } else {
                setSelectedKeywords([]);
                setSelectedPurpose(null);
                setShowPurposeField(false);
                setShowPurposeError(false);
            }
        }
    }, [selectedProject, loadProjectSettings]);

    // Refresh attachments when mailbox item changes
    useEffect(() => {
        const prevMailboxItem = prevMailboxItemRef.current;
        prevMailboxItemRef.current = props.mailboxItem;

        if (props.mailboxItem && prevMailboxItem !== props.mailboxItem) {
            Log.info("mailboxItem change — refreshing attachments");
            populateAttachments();
            setSelectedProjectAttachments([]);
            populateFromAndTo();
            setSubject(props.mailboxItem.subject ?? "");
            populateRemarks();
        }
    }, [props.mailboxItem, populateAttachments, populateFromAndTo, populateRemarks]);

    const onOpen = useCallback(() => {
        Log.info("Loading new file transfer panel...");
    }, []);

    const handleClear = useCallback(() => {
        const currentUserEmail = props.officeWrapper.userProfileEmailAddress;
        const nextFrom = currentUserEmail ? [{ text: currentUserEmail }] : [];
        setSelectedKeywords([]);
        setSelectedPurpose(null);
        setShowPurposeField(false);
        setShowPurposeError(false);
        setSelectedProject(undefined);
        setSubject("");
        setShowSubjectError(false);
        setRemarks("");
        setAttachments([]);
        setSelectedProjectAttachments([]);
        setFrom(nextFrom);
        setTo([]);
        setCc([]);
        setShouldClearEmails(true);
        setAreToAndCCSame(false);
        setShowAttachmentError(false);
        setShowProjectError(false);
    }, [props.officeWrapper]);

    const handleClose = useCallback(() => {
        if (!isSubmitting) {
            setAttachments([]);
            setSelectedProjectAttachments([]);
            setFrom([]);
            setTo([]);
            setCc([]);
            setShouldClearEmails(false);
            setAreToAndCCSame(false);
            setShowAttachmentError(false);
            setShowProjectError(false);
        }
        props.onClose();
    }, [props, isSubmitting]);

    const handleSubmit = useCallback(async () => {
        if (isSubmitting) return;

        let hasValidationError = false;

        if (!selectedProject) {
            setShowProjectError(true);
            hasValidationError = true;
        }

        if (!subject.trim()) {
            setShowSubjectError(true);
            hasValidationError = true;
        }

        if (attachments.length === 0 && selectedProjectAttachments.length === 0) {
            setShowAttachmentError(true);
            hasValidationError = true;
        }

        if (showPurposeField && !selectedPurpose) {
            setShowPurposeError(true);
            hasValidationError = true;
        }

        if (hasValidationError) {
            return;
        }

        // Total shown in the progress page; includes project-folder files, which are not uploaded to S3.
        const attachmentCount = attachments.length + selectedProjectAttachments.length;
        const totalFiles = Math.max(attachmentCount, 1);
        isCancelRequestedRef.current = false;
        isTransferFinishedRef.current = false;
        setFileUploadCount(totalFiles);
        // Project files are not uploaded (already in the project), so count them as complete up front.
        setFileUploadCompletedCount(selectedProjectAttachments.length);
        setIsProjectFilesOnlyTransfer(attachments.length === 0 && selectedProjectAttachments.length > 0);
        setIsSubmitting(true);
        setShowAttachmentError(false);

        try {
            DateHelper.synchNow();

            const messageNrn = props.officeWrapper.getCurrentMessageNrn();
            const mappedFrom = props.formValidationHelpers.mapPersonaPropsToContact(from);
            const mappedTo = props.formValidationHelpers.mapPersonaPropsToContact(to);
            const mappedCc = props.formValidationHelpers.mapPersonaPropsToContact(cc);

            const fileTransferParams: LogFileTransferParams = {
                from: mappedFrom[0],
                to: mappedTo,
                cc: mappedCc,
                subject: subject,
                remarks: remarks,
                keywords: selectedKeywords,
                expiresOn: moreOptionsSettings?.expireDateSelection.Checked
                    ? moreOptionsSettings.expireDateSelection.SelectedDate &&
                      DateHelper.adjustTime(moreOptionsSettings.expireDateSelection.SelectedDate)
                    : undefined,
                reminderOn: moreOptionsSettings?.reminderDateSelection.Checked
                    ? moreOptionsSettings.reminderDateSelection.SelectedDate
                    : undefined,
                allowPartialDownload: moreOptionsSettings?.allowPartialDownload ?? false,
                requireNixLoginForDownload: moreOptionsSettings?.requireNixLoginForDownload ?? false,
                transferWithTransmittal: showPurposeField,
                attachments: [], // this gets set in fileAsFileTransfer after mapping with projectNrn
                purpose:
                    showPurposeField && selectedPurpose
                        ? { name: selectedPurpose.name, type: selectedPurpose.type }
                        : undefined,
            };

            if (selectedProject) {
                await props.fileTransferApiService.fileAsFileTransfer(
                    selectedProject.nrn,
                    fileTransferParams,
                    messageNrn,
                    [...attachments, ...selectedProjectAttachments],
                    (_isInProgress: boolean, _failedIds: string[]) => {},
                    () => {
                        setFileUploadCompletedCount((prev) => Math.min(prev + 1, totalFiles));
                    },
                    props.isNL1221Supported
                );
                isTransferFinishedRef.current = true;
                setIsCancelDialogOpen(false);
                props.onShowToast(props.translate("FLUENT9.FILE_TRANSFER_SUCCESS") as string, "success");
            }
        } catch (error) {
            Log.error("error", error);
            if (!isCancelRequestedRef.current) {
                props.onShowToast(props.translate("FLUENT9.FILE_TRANSFER_FAILURE") as string, "error");
            }
        } finally {
            Log.info("done");
            setIsCancelInProgress(false);
            setIsSubmitting(false);
        }
    }, [
        attachments,
        selectedProjectAttachments,
        cc,
        selectedPurpose,
        from,
        isSubmitting,
        moreOptionsSettings,
        props,
        projectsContext,
        selectedProject,
        selectedProjectAttachments,
        selectedKeywords,
        showPurposeField,
        subject,
        remarks,
        to,
    ]);

    const selectedProjectTag: ITag | null = selectedProject
        ? {
              key: selectedProject.nrn,
              name: selectedProject.displayName,
          }
        : null;

    const isCcValid = cc.length === 0 || props.formValidationHelpers.areAssigneesValid(cc, true);
    const isFormValid =
        !!selectedProject &&
        !isLoadingProjectSettings &&
        !!subject.trim() &&
        props.formValidationHelpers.areAssigneesValid(to, true) &&
        isCcValid &&
        (!showPurposeField || !!selectedPurpose) &&
        (attachments.length > 0 || selectedProjectAttachments.length > 0) &&
        !areToAndCCSame;

    const handleCancelFileTransfer = useCallback(() => {
        setIsCancelDialogOpen(true);
    }, []);

    const handleDismissCancelDialog = useCallback(() => {
        if (isCancelInProgress) {
            return;
        }
        setIsCancelDialogOpen(false);
    }, [isCancelInProgress]);

    const handleConfirmCancelFileTransfer = useCallback(async () => {
        if (isCancelInProgress || !isSubmitting) {
            return;
        }

        if (isTransferFinishedRef.current) {
            setIsCancelDialogOpen(false);
            return;
        }

        setIsCancelInProgress(true);
        isCancelRequestedRef.current = true;

        try {
            await props.fileTransferApiService.abortFileTransfer();
        } catch (error) {
            Log.error("Failed to abort file transfer", error);
        }

        if (isTransferFinishedRef.current) {
            setIsCancelInProgress(false);
            setIsCancelDialogOpen(false);
            return;
        }

        setIsCancelDialogOpen(false);
        setIsSubmitting(false);
        setIsCancelInProgress(false);
        props.onShowToast(props.translate("APP.FILE_TRANSFER.CANCEL_ERROR") as string, "error");
    }, [isCancelInProgress, isSubmitting, props]);

    const panelTitle = isSubmitting
        ? (props.translate("FLUENT9.FILE_TRANSFER_IN_PROGRESS") as string)
        : (props.translate("APP.COMMANDBAR.FILE_TRANSFER") as string);

    return (
        <NewformaPanel
            title={panelTitle}
            testid="file-transfer-panel"
            isOpen={props.isOpen}
            onOpen={onOpen}
            onClose={handleClose}
            footer={
                isSubmitting ? (
                    <></>
                ) : (
                    <>
                        <NewformaButton
                            testid="file-transfer-panel-clear-button"
                            onClick={handleClear}
                            disabled={isSubmitting}
                            appearance="subtle"
                            style={{ marginRight: "auto" }}
                        >
                            {props.translate("FLUENT9.CLEAR_FORM") as string}
                        </NewformaButton>
                        <NewformaButton
                            testid="file-transfer-panel-close-button"
                            onClick={handleClose}
                            appearance="outline"
                        >
                            {props.translate("FLUENT9.CLOSE") as string}
                        </NewformaButton>
                        <NewformaButton
                            testid="file-transfer-panel-submit-button"
                            disabled={!isFormValid}
                            onClick={handleSubmit}
                            appearance="primary"
                        >
                            {props.translate("FLUENT9.SUBMIT") as string}
                        </NewformaButton>
                    </>
                )
            }
        >
            <>
                {isSubmitting && (
                    <>
                        <FileTransferProgressPage
                            fileUploadCompletedCount={fileUploadCompletedCount}
                            fileUploadCount={fileUploadCount}
                            onCancel={handleCancelFileTransfer}
                            hideCancel={isProjectFilesOnlyTransfer}
                        />
                        <CancelFileTransferDialog
                            isOpen={isCancelDialogOpen}
                            isCancelInProgress={isCancelInProgress}
                            onDismiss={handleDismissCancelDialog}
                            onConfirm={handleConfirmCancelFileTransfer}
                        />
                    </>
                )}
                <div className={styles.formFields} style={isSubmitting ? { display: "none" } : undefined}>
                    <ProjectPickerFluent9
                        smartFilingManager={props.smartFilingManager}
                        invalidateCacheService={props.invalidateCacheService}
                        logger={props.logger}
                        officeWrapper={props.officeWrapper}
                        showValidationError={showProjectError}
                        selectedProject={selectedProject}
                        onProjectChange={(project) => {
                            setSelectedProject(project);
                            if (project) {
                                setShouldClearEmails(false);
                            }
                        }}
                    />
                    <NewformaInput
                        label={props.translate("APP.FILE_TRANSFER.SUBJECT_LABEL") as string}
                        required={true}
                        validationMessage={
                            showSubjectError ? (props.translate("SHARED.FIELD.REQUIRED_ERROR") as string) : undefined
                        }
                        validationState={showSubjectError ? "error" : "none"}
                        fieldDataTestId="file-transfer-subject-field"
                        data-testid="file-transfer-subject-input"
                        value={subject}
                        onChange={(_e, data) => {
                            setSubject(data.value);
                            if (data.value.trim()) {
                                setShowSubjectError(false);
                            }
                        }}
                        disabled={isSubmitting}
                        maxLength={255}
                        autoComplete="off"
                        placeholder={props.translate("APP.FILE_TRANSFER.SUBJECT_PLACEHOLDER") as string}
                    />
                    <PurposeDropdownFluent9
                        selectedProject={selectedProject}
                        projectsService={props.projectsService}
                        logger={props.logger}
                        disabled={isSubmitting}
                        showPurposeField={showPurposeField}
                        selectedPurpose={selectedPurpose}
                        onPurposeChange={(purpose) => {
                            setSelectedPurpose(purpose);
                            if (purpose) {
                                setShowPurposeError(false);
                            }
                        }}
                        showValidationError={showPurposeError}
                        testid="file-transfer-purpose"
                    />
                    <FromAndToFluent9
                        key="fileTransferFluent9"
                        isFromRequired={false}
                        isToRequired={true}
                        isCcRequired={false}
                        includeCc={true}
                        officeWrapper={props.officeWrapper}
                        disabled={isSubmitting || !selectedProjectTag}
                        formValidationHelpers={props.formValidationHelpers}
                        mailboxItem={props.mailboxItem}
                        onFromChange={(people) => {
                            setFrom(people);
                            setShouldClearEmails(false);
                        }}
                        onToChange={(people) => {
                            setTo(people);
                            setShouldClearEmails(false);
                        }}
                        onCcChange={(people) => {
                            setCc(people);
                            setShouldClearEmails(false);
                        }}
                        logger={props.logger}
                        projectsService={props.projectsService}
                        project={selectedProjectTag}
                        shouldHideFrom={true}
                        shouldClearEmails={shouldClearEmails}
                        isFileTransferComponent={true}
                        onEmailCheck={setAreToAndCCSame}
                        from={from}
                        to={to}
                        cc={cc}
                    />
                    <RemarksFluent9
                        value={remarks}
                        onChange={setRemarks}
                        disabled={isSubmitting}
                        isNL1221Supported={props.isNL1221Supported}
                        configService={props.configService}
                        testid="file-transfer-remarks"
                    />
                    <KeywordsFluent9
                        label={props.translate("APP.FILE_TRANSFER.KEYWORDS_LABEL") as string}
                        required={false}
                        disabled={isSubmitting || !selectedProjectTag}
                        selectedProject={selectedProject}
                        projectsService={props.projectsService}
                        keywordListType={KeywordListType.TransmittalKeywords}
                        logger={props.logger}
                        value={selectedKeywords}
                        onChange={(updatedSelectedKeywords) => {
                            setSelectedKeywords(updatedSelectedKeywords);
                        }}
                        testid="file-transfer-keywords"
                    />
                    <AttachmentsFluent9
                        attachments={attachments}
                        populateKey={attachmentsPopulateKey}
                        disabled={isSubmitting}
                        onAttachmentsUpdated={(updatedAttachments) => {
                            setAttachments(updatedAttachments);
                            setShowAttachmentError(false);
                        }}
                        logger={props.logger}
                        allowFileUploads={true}
                        required={true}
                        isFilePathInAttachmentsSupported={props.isFilePathInAttachmentsSupported}
                        isNL1221Supported={props.isNL1221Supported}
                        showValidationError={showAttachmentError}
                        totalAttachmentCount={attachments.length + selectedProjectAttachments.length}
                        projectFilesSection={
                            props.isSendFilesFromProjectSupported
                                ? {
                                      smartFilingManager: props.smartFilingManager,
                                      invalidateCacheService: props.invalidateCacheService,
                                      logger: props.logger,
                                      officeWrapper: props.officeWrapper,
                                      projectsService: props.projectsService,
                                      disabled: isSubmitting,
                                      selectedProjectAttachments,
                                      onProjectAttachmentsUpdated: (files) => {
                                          setSelectedProjectAttachments(files);
                                          setShowAttachmentError(false);
                                      },
                                  }
                                : undefined
                        }
                    />
                    <MoreOptionsFluent9Component
                        disabled={isSubmitting || !selectedProjectTag}
                        noProjectSelected={!selectedProjectTag}
                        projectConfig={moreOptionsProjectConfig}
                        moreOptionsSettings={moreOptionsSettings}
                        storageWrapper={props.storageWrapper}
                        isLoading={isLoadingProjectSettings}
                        onSettingsChange={(partial) => setMoreOptionsSettings((prev) => ({ ...prev, ...partial }))}
                    />
                </div>
            </>
        </NewformaPanel>
    );
};

export default withLocalize(FileTransferFluent9ComponentHooks);
